import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Check, ChevronsUpDown, Globe } from "lucide-react";
import { useWebsite } from "../contexts/WebsiteContext";

export default function WebsiteSwitcher() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { websiteId } = useParams();
  const { websites, selectedWebsite, setSelectedWebsite, isLoading } =
    useWebsite();

  useEffect(() => {
    if (websiteId) {
      const website = websites.find((w) => w.id === Number(websiteId));
      if (website && website !== selectedWebsite) {
        setSelectedWebsite(website);
      }
    } else if (!websiteId && selectedWebsite) {
      setSelectedWebsite(null); // Reset to all websites mode
    }
  }, [websiteId, websites, selectedWebsite, setSelectedWebsite]);

  const isDashboardPage = location.pathname.startsWith("/dashboard");
  const isAggregateView = location.pathname === "/dashboard";
  const currentWebsite = websiteId
    ? websites.find((w) => w.id === Number(websiteId))
    : null;
  const displayText = isAggregateView
    ? "All Websites"
    : currentWebsite?.website_name ||
    selectedWebsite?.website_name ||
    "Select website";

  if (isLoading) {
    return (
      <div className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4" />
          <span className="text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  if (websites.length === 0) {
    return (
      <div className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4" />
          <span className="text-muted-foreground">No websites added</span>
        </div>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          aria-label="Select a website"
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            <span className="truncate">{displayText}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search websites..." />
          <CommandList>
            <CommandEmpty>No website found.</CommandEmpty>
            {isDashboardPage && (
              <>
                <CommandGroup heading="View Options">
                  <CommandItem
                    onSelect={() => {
                      setSelectedWebsite(null);
                      setOpen(false);
                      navigate("/dashboard");
                    }}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      {isAggregateView && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                      <div>
                        <p className="text-sm">All Websites</p>
                        <p className="text-xs text-muted-foreground">
                          View aggregate data
                        </p>
                      </div>
                    </div>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup heading="Your Websites">
              {websites.map((website) => (
                <CommandItem
                  key={website.id}
                  onSelect={() => {
                    setSelectedWebsite(website);
                    setOpen(false);
                    if (isDashboardPage) {
                      navigate(`/dashboard/${website.id}`);
                    } else {
                      navigate(`/manage-plan/${website.id}`);
                    }
                  }}
                  className="cursor-pointer"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="text-sm">
                            {website.website_name || "Unnamed Website"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {website.domain || "No domain"}
                          </p>
                        </div>
                      </div>
                    </div>
                    {(currentWebsite?.id === website.id ||
                      selectedWebsite?.id === website.id) &&
                      !isAggregateView && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
